import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserPreferences } from '@/lib/types/user/preferences'
import { DEFAULT_PREFERENCES } from '@/lib/constants/preferences-client'

/**
 * Preferences slice for managing user preferences in Redux store
 * This is primarily for optimistic updates and caching - the source of truth
 * is the preference manager on the server
 */

interface PreferencesState {
  preferences: UserPreferences | null
  isLoading: boolean
  error: string | null
}

const initialState: PreferencesState = {
  preferences: null,
  isLoading: false,
  error: null
}

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    // Set complete preferences (e.g., after loading from server)
    set: (state, action: PayloadAction<UserPreferences>) => {
      state.preferences = action.payload
      state.error = null
    },

    // Update partial preferences (merge with existing)
    update: (state, action: PayloadAction<Partial<UserPreferences>>) => {
      if (!state.preferences) {
        state.preferences = { ...DEFAULT_PREFERENCES } as UserPreferences
      }
      const merged = deepMerge(state.preferences as UserPreferences, action.payload)
      state.preferences = merged as UserPreferences
      state.error = null
    },

    // Update specific UI preferences
    updateUI: (state, action: PayloadAction<Partial<UserPreferences['ui']>>) => {
      if (!state.preferences) {
        state.preferences = { ...DEFAULT_PREFERENCES } as UserPreferences
      }
      if (state.preferences) {
        state.preferences.ui = { ...state.preferences.ui, ...action.payload }
        state.error = null
      }
    },

    // Update specific notification preferences
    updateNotifications: (state, action: PayloadAction<Partial<UserPreferences['notifications']>>) => {
      if (!state.preferences) {
        state.preferences = { ...DEFAULT_PREFERENCES } as UserPreferences
      }
      if (state.preferences) {
        state.preferences.notifications = {
          ...state.preferences.notifications,
          ...action.payload,
          quietHours: {
            ...state.preferences.notifications.quietHours,
            ...(action.payload.quietHours || {})
          }
        }
        state.error = null
      }
    },

    // Update specific application preferences
    updateApplication: (state, action: PayloadAction<Partial<UserPreferences['application']>>) => {
      if (!state.preferences) {
        state.preferences = { ...DEFAULT_PREFERENCES } as UserPreferences
      }
      if (state.preferences) {
        state.preferences.application = { ...state.preferences.application, ...action.payload }
        state.error = null
      }
    },

    // Update specific localization preferences
    updateLocalization: (state, action: PayloadAction<Partial<UserPreferences['localization']>>) => {
      if (!state.preferences) {
        state.preferences = { ...DEFAULT_PREFERENCES } as UserPreferences
      }
      if (state.preferences) {
        state.preferences.localization = { ...state.preferences.localization, ...action.payload }
        state.error = null
      }
    },

    // Clear preferences (e.g., on logout)
    clear: (state) => {
      state.preferences = null
      state.error = null
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    }
  }
})

// Deep merge helper function
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target } as any

  Object.keys(source).forEach((key) => {
    const sourceValue = (source as any)[key]
    const targetValue = (target as any)[key]

    if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
      if (targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        result[key] = deepMerge(targetValue, sourceValue)
      } else {
        result[key] = sourceValue
      }
    } else {
      result[key] = sourceValue
    }
  })

  return result as T
}

export const {
  set: setPreferences,
  update: updatePreferences,
  updateUI: updateUIPreferences,
  updateNotifications: updateNotificationPreferences,
  updateApplication: updateApplicationPreferences,
  updateLocalization: updateLocalizationPreferences,
  clear: clearPreferences,
  setLoading: setPreferencesLoading,
  setError: setPreferencesError
} = preferencesSlice.actions

export default preferencesSlice.reducer
